import voca from "voca";
import { DateTime } from "luxon";
import numeral from "numeral";
import { luxonify } from "@teaming/common-util";
import { KeyResultDisplayFormat } from "@teaming/common-gokr";
import {
	Direction,
	GoalActivityType,
	KeyResultActivityType,
	KeyResultType
} from "@teaming/common-enums";

export const SHORT_DATE_FORMAT = "MM/dd/yyyy";

export const mapGoalToForm = goal => {
	const {
		id: goalId,
		title,
		plannedStartDate: startDate,
		plannedFinishDate: endDate,
		teamId: assignedTeamId,
		teamName: assignedTeamName,
		confidence: confidenceLevel,
		lastUpdated: lastUpdatedDate,
		percentCompleted: progress,
		percentProgressed: pacing,
		archived,
		created: createdDate,
		createdBy: creator,
	} = goal;

	const {
		personId: creatorId,
		displayName: creatorName,
	} = creator;

	return {
		goalId,
		title,
		startDate: luxonify( startDate ).toFormat( SHORT_DATE_FORMAT ),
		endDate: luxonify( endDate ).toFormat( SHORT_DATE_FORMAT ),
		endDateTime: luxonify( endDate ).toISO(),
		assignedTeamId,
		assignedTeamName,
		confidenceLevel,
		// need the full date/time for timeago
		lastUpdatedDate: luxonify( lastUpdatedDate ).toISO(),
		pacing,
		progress,
		keyResultCount: goal.keyResults.length,
		isArchived: !!archived,
		createdDate: luxonify( createdDate ).toFormat( SHORT_DATE_FORMAT ),
		creatorId,
		creatorName,
	};
};

const NO_OWNER = {
	personId: "",
	displayName: "",
	picture: "",
	email: "",
};

export const mapKeyResultToForm = keyResult => {
	const {
		id: keyResultId,
		goalId,
		teamId,
		sortOrder,
		name,
		confidence: keyResultConfidenceLevel,
		type,
		createdBy: creator,
		owners,
		created: createdDate,
		lastUpdated: lastUpdatedDate,
		percentCompleted: progress,
		percentProgressed: pacing,
	} = keyResult;

	let {
		currentValue,
		goalValue: targetValue,
	} = keyResult;

	// unwrap numeral
	if ( currentValue.value ) {
		currentValue = currentValue.value();
	}

	// unwrap numeral
	if ( targetValue.value ) {
		targetValue = targetValue.value();
	}

	const {
		personId: creatorId,
		displayName: creatorName,
	} = creator;

	const {
		personId: ownerId,
		displayName: ownerName,
		picture: ownerAvatar,
		email: ownerEmail,
	} = ( owners[ 0 ] || NO_OWNER );

	return {
		keyResultId,
		goalId,
		teamId,
		sortOrder,
		name,
		confidenceLevel: keyResultConfidenceLevel,
		type,
		currentValue: `${ currentValue }`,
		targetValue: `${ targetValue }`,
		creatorId,
		creatorName,
		ownerId,
		ownerName,
		ownerAvatar,
		ownerEmail,
		createdDate, // ISO
		lastUpdatedDate, // ISO
		lastUpdatedAgo: DateTime.fromISO( lastUpdatedDate )
			.toRelative(), // e.g. "16 hours ago"
		pacing,
		progress,
	};
};

export const mapFormToKeyResult = keyResultForm => {
	const {
		goalId,
		keyResultId,
		name,
		type,
		ownerId,
		confidenceLevel: keyResultConfidenceLevel,
		sortOrder,
	} = keyResultForm;

	let {
		currentValue,
		targetValue: goalValue,
	} = keyResultForm;

	if ( type !== KeyResultType.BINARY ) {
		currentValue = numeral( currentValue ).value();
		goalValue = numeral( goalValue ).value();
	}

	const keyResult = {
		name,
		type,
		currentValue,
		goalValue,
		owners: [ ownerId, ],
		confidence: keyResultConfidenceLevel,
		sortOrder,
	};

	if ( keyResultId ) {
		keyResult.id = keyResultId;
	}

	if ( goalId ) {
		keyResult.goalId = goalId;
	}

	return keyResult;
};

export const mapFormToGoal = ( goalForm, keyResultForms = [] ) => {
	const {
		goalId,
		title,
		assignedTeamId: teamId,
		startDate,
		endDate,
		confidenceLevel: goalConfidenceLevel,
	} = goalForm;

	let keyResults = [];
	if ( keyResultForms.length ) {
		keyResults = keyResultForms.map( ( keyResultForm, index ) => {
			return {
				...mapFormToKeyResult( keyResultForm ),
				sortOrder: index,
			};
		} );
	}

	const goal = {
		title,
		teamId,
		plannedStartDate: DateTime.fromFormat( startDate, SHORT_DATE_FORMAT )
			.startOf( "day" )
			.toUTC()
			.toISO(),
		plannedFinishDate: DateTime.fromFormat( endDate, SHORT_DATE_FORMAT )
			.startOf( "day" )
			.toUTC()
			.toISO(),
		confidence: goalConfidenceLevel,
		keyResults,
	};

	if ( goalId ) {
		goal.id = goalId;
	}

	return goal;
};

export const mapActivityToFeed = ( goalActivity, timezone ) => {
	const percentFormatter = KeyResultDisplayFormat[ KeyResultType.PERCENTAGE ];
	const computeCompletion = tuple => {
		const [ currentValue, goalValue, ] = tuple;
		const percent = Math.floor( ( currentValue / goalValue ) * 100 );
		return percentFormatter( percent );
	};

	const patchConfidence = activity => {
		const { activityType, } = activity;
		if ( !( activityType === GoalActivityType.CONFIDENCE, KeyResultActivityType.CONFIDENCE ) ) {
			return activity;
		}
		return {
			...activity,
			direction: activity.direction > 0 ?
				Direction.UP :
				Direction.DOWN,
		};
	};

	const patchDates = activity => {
		const { discriminator, activityType, to, from, } = activity;
		if ( discriminator !== "GOAL" ) {
			return activity;
		}
		if ( !( activityType === GoalActivityType.DUE_DATE || activityType === GoalActivityType.START_DATE ) ) {
			return activity;
		}
		return {
			...activity,
			to: DateTime.fromISO( to )
				.setZone( timezone )
				.toFormat( SHORT_DATE_FORMAT ),
			from: DateTime.fromISO( from )
				.setZone( timezone )
				.toFormat( SHORT_DATE_FORMAT ),
		};
	};

	const patchProgress = activity => {
		const { discriminator, activityType, type, to, from, } = activity;
		if ( !( discriminator === "KEY_RESULT" && activityType === KeyResultActivityType.PROGRESS ) ) {
			return activity;
		}
		const formatter = KeyResultDisplayFormat[ type ];

		const [ currentTo, goalTo, ] = to.map( formatter );
		const [ currentFrom, goalFrom, ] = from.map( formatter );

		if ( type !== KeyResultType.BINARY ) {
			const completedTo = computeCompletion( to );
			const completedFrom = computeCompletion( from );
			return {
				...activity,
				to: `${ currentTo }/${ goalTo } (${ completedTo })`,
				from: `${ currentFrom }/${ goalFrom } (${ completedFrom })`,
			};
		}

		// only show the current value for binary KR activity
		return {
			...activity,
			to: `${ currentTo }`,
			from: `${ currentFrom }`,
		};
	};

	const patchKeyResultType = activity => {
		const { discriminator, activityType, to, from, } = activity;
		if ( !( discriminator === "KEY_RESULT" && activityType === KeyResultActivityType.TYPE ) ) {
			return activity;
		}
		return {
			...activity,
			to: voca.titleCase( to ),
			from: voca.titleCase( from ),
		};
	};

	const patchActivity = ( activity, index, { length, } ) => {
		activity = patchConfidence( activity );
		activity = patchDates( activity );
		activity = patchProgress( activity );
		activity = patchKeyResultType( activity );

		return {
			...activity,
			changedAt: DateTime.fromISO( activity.changedAt )
				.setZone( timezone )
				.toFormat( SHORT_DATE_FORMAT ),
			isLast: ( index === length - 1 ),
		};
	};

	const filteredActivity = goalActivity.filter( activity => {
		return !( activity.discriminator === "KEY_RESULT" &&
			activity.activityType === KeyResultActivityType.SORT_ORDER );
	} );

	return filteredActivity.map( patchActivity );
};
